import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ImprintModal from './ImprintModal';
import DataProtectionModal from './DataProtectionModal';
import TermsModal from './TermsModal';
import ChatBot from './ChatBot';

// Define props interface for Footer
interface FooterProps {
  language?: string;
}

// Define translations for Footer content
const TRANSLATIONS: Record<string, {
  imprint: string;
  dataProtection: string;
  termsAndConditions: string;
  businessAssistant: string;
  madeInGermanyHistory: string;
  careers: string;
  franchiseSystem: string;
  legalAndProtection: string;
  pressContact: string;
  strategicInvestments: string;
  copyright: string;
}> = {
  en: {
    imprint: "IMPRINT",
    dataProtection: "DATA PROTECTION",
    termsAndConditions: "TERMS AND CONDITIONS",
    businessAssistant: "BUSINESS ASSISTANT",
    madeInGermanyHistory: "MADE-IN-GERMANY-HISTORY",
    careers: "CAREERS",
    franchiseSystem: "FRANCHISE-SYSTEM",
    legalAndProtection: "LEGAL AND PROTECTION",
    pressContact: "PRESS CONTACT",
    strategicInvestments: "STRATEGIC INVESTMENTS",
    copyright: "Copyright 2025 All rights reserved - Based in Europe and available worldwide - MADE-IN-GERMANY ©"
  },
  de: {
    imprint: "IMPRESSUM",
    dataProtection: "DATENSCHUTZ",
    termsAndConditions: "ALLGEMEINE GESCHÄFTSBEDINGUNGEN",
    businessAssistant: "GESCHÄFTSASSISTENT",
    madeInGermanyHistory: "MADE-IN-GERMANY-GESCHICHTE",
    careers: "KARRIERE",
    franchiseSystem: "FRANCHISE-SYSTEM",
    legalAndProtection: "RECHTLICHES UND SCHUTZ",
    pressContact: "PRESSEKONTAKT",
    strategicInvestments: "STRATEGISCHE INVESTITIONEN",
    copyright: "Copyright 2025 Alle Rechte vorbehalten - Ansässig in Europa und weltweit verfügbar - MADE-IN-GERMANY ©"
  },
  es: {
    imprint: "IMPRENTA",
    dataProtection: "PROTECCIÓN DE DATOS",
    termsAndConditions: "TÉRMINOS Y CONDICIONES",
    businessAssistant: "ASISTENTE DE NEGOCIOS",
    madeInGermanyHistory: "HISTORIA DE MADE-IN-GERMANY",
    careers: "CARRERAS",
    franchiseSystem: "SISTEMA DE FRANQUICIAS",
    legalAndProtection: "LEGAL Y PROTECCIÓN",
    pressContact: "CONTACTO DE PRENSA",
    strategicInvestments: "INVERSIONES ESTRATÉGICAS",
    copyright: "Copyright 2025 Todos los derechos reservados - Con sede en Europa y disponible en todo el mundo - MADE-IN-GERMANY ©"
  },
  fr: {
    imprint: "MENTIONS LÉGALES",
    dataProtection: "PROTECTION DES DONNÉES",
    termsAndConditions: "TERMES ET CONDITIONS",
    businessAssistant: "ASSISTANT COMMERCIAL",
    madeInGermanyHistory: "HISTOIRE DE MADE-IN-GERMANY",
    careers: "CARRIÈRES",
    franchiseSystem: "SYSTÈME DE FRANCHISE",
    legalAndProtection: "LÉGAL ET PROTECTION",
    pressContact: "CONTACT PRESSE",
    strategicInvestments: "INVESTISSEMENTS STRATÉGIQUES",
    copyright: "Copyright 2025 Tous droits réservés - Basé en Europe et disponible dans le monde entier - MADE-IN-GERMANY ©"
  },
  it: {
    imprint: "IMPRESSUM",
    dataProtection: "PROTEZIONE DEI DATI",
    termsAndConditions: "TERMINI E CONDIZIONI",
    businessAssistant: "ASSISTENTE AZIENDALE",
    madeInGermanyHistory: "STORIA DI MADE-IN-GERMANY",
    careers: "CARRIERE",
    franchiseSystem: "SISTEMA DI FRANCHISING",
    legalAndProtection: "LEGALE E PROTEZIONE",
    pressContact: "CONTATTO STAMPA",
    strategicInvestments: "INVESTIMENTI STRATEGICI",
    copyright: "Copyright 2025 Tutti i diritti riservati - Con sede in Europa e disponibile in tutto il mondo - MADE-IN-GERMANY ©"
  },
  nl: {
    imprint: "IMPRESSUM",
    dataProtection: "GEGEVENSBESCHERMING",
    termsAndConditions: "ALGEMENE VOORWAARDEN",
    businessAssistant: "BEDRIJFSASSISTENT",
    madeInGermanyHistory: "MADE-IN-GERMANY-GESCHIEDENIS",
    careers: "CARRIÈRES",
    franchiseSystem: "FRANCHISESYSTEEM",
    legalAndProtection: "JURIDISCH EN BESCHERMING",
    pressContact: "PERSCONTACT",
    strategicInvestments: "STRATEGISCHE INVESTERINGEN",
    copyright: "Copyright 2025 Alle rechten voorbehouden - Gevestigd in Europa en wereldwijd beschikbaar - MADE-IN-GERMANY ©"
  },
  sa: {
    imprint: "بصمة",
    dataProtection: "حماية البيانات",
    termsAndConditions: "الشروط والأحكام",
    businessAssistant: "مساعد الأعمال",
    madeInGermanyHistory: "تاريخ MADE-IN-GERMANY",
    careers: "وظائف",
    franchiseSystem: "نظام الامتياز",
    legalAndProtection: "قانوني وحماية",
    pressContact: "اتصال الصحافة",
    strategicInvestments: "استثمارات استراتيجية",
    copyright: "حقوق الطبع والنشر 2025 جميع الحقوق محفوظة - مقرها في أوروبا ومتوفرة في جميع أنحاء العالم - MADE-IN-GERMANY ©"
  },
  hk: {
    imprint: "版權聲明",
    dataProtection: "數據保護",
    termsAndConditions: "條款與條件",
    businessAssistant: "商業助理",
    madeInGermanyHistory: "MADE-IN-GERMANY歷史",
    careers: "職業",
    franchiseSystem: "特許經營系統",
    legalAndProtection: "法律與保護",
    pressContact: "新聞聯絡",
    strategicInvestments: "策略投資",
    copyright: "版權 2025 保留所有權利 - 總部位於歐洲，全球可用 - MADE-IN-GERMANY ©"
  },
  sg: {
    imprint: "版权声明",
    dataProtection: "数据保护",
    termsAndConditions: "条款与条件",
    businessAssistant: "商业助理",
    madeInGermanyHistory: "MADE-IN-GERMANY历史",
    careers: "职业",
    franchiseSystem: "特许经营系统",
    legalAndProtection: "法律与保护",
    pressContact: "新闻联络",
    strategicInvestments: "战略投资",
    copyright: "版权 2025 保留所有权利 - 总部位于欧洲，全球可用 - MADE-IN-GERMANY ©"
  },
  za: {
    imprint: "AFDRUK",
    dataProtection: "GEGEVENSBESKERMING",
    termsAndConditions: "TERME EN VOORWAARDES",
    businessAssistant: "BESIGHEIDSASSISTENT",
    madeInGermanyHistory: "MADE-IN-GERMANY-GESKIEDENIS",
    careers: "LOOPBANE",
    franchiseSystem: "FRANCHISE-STELSEL",
    legalAndProtection: "WETTIG EN BESKERMING",
    pressContact: "PERSKONTAK",
    strategicInvestments: "STRATEGIESE BELEGGINGS",
    copyright: "Kopiereg 2025 Alle regte voorbehou - Gevestig in Europa en wêreldwyd beskikbaar - MADE-IN-GERMANY ©"
  },
  kr: {
    imprint: "임프린트",
    dataProtection: "데이터 보호",
    termsAndConditions: "이용 약관",
    businessAssistant: "비즈니스 어시스턴트",
    madeInGermanyHistory: "MADE-IN-GERMANY 역사",
    careers: "커리어",
    franchiseSystem: "프랜차이즈 시스템",
    legalAndProtection: "법률 및 보호",
    pressContact: "언론 연락처",
    strategicInvestments: "전략적 투자",
    copyright: "저작권 2025 모든 권리 보유 - 유럽에 기반을 두고 전 세계에서 이용 가능 - MADE-IN-GERMANY ©"
  },
  jp: {
    imprint: "インプリント",
    dataProtection: "データ保護",
    termsAndConditions: "利用規約",
    businessAssistant: "ビジネスアシスタント",
    madeInGermanyHistory: "MADE-IN-GERMANYの歴史",
    careers: "キャリア",
    franchiseSystem: "フランチャイズシステム",
    legalAndProtection: "法的および保護",
    pressContact: "報道関係連絡先",
    strategicInvestments: "戦略的投資",
    copyright: "著作権 2025 すべての権利を保有 - ヨーロッパに拠点を置き、世界中で利用可能 - MADE-IN-GERMANY ©"
  }
};

const Footer: React.FC<FooterProps> = ({ language = 'en' }) => {
  const [isImprintOpen, setIsImprintOpen] = useState<boolean>(false);
  const [isDataProtectionOpen, setIsDataProtectionOpen] = useState<boolean>(false);
  const [isTermsOpen, setIsTermsOpen] = useState<boolean>(false);
  const [isChatBotOpen, setIsChatbotOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const translations = TRANSLATIONS[language] || TRANSLATIONS['en'];

  const openImprintModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsImprintOpen(true);
  };

  const openDataProtectionModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDataProtectionOpen(true);
  };

  const openTermsModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsTermsOpen(true);
  };

  const openChatBot = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsChatbotOpen(true);
  };

  const handleMadeInGermanyHistoryClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/madeingermanyhistory');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCareersClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/careers');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFranchiseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/comingsoon');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLegalAndProtectionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/legalandprotection');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePressContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/presscontact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStrategicInvestmentsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/strategicinvestments');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <>
      <footer className="bg-[#0B111F] text-white">
        <div className="text-center py-6 px-4 sm:py-2 sm:px-0">
          {/* Mobile: 5 lines */}
          <nav className="text-sm flex flex-col gap-1 sm:hidden">
            <div className="flex justify-center items-center gap-2">
              <button onClick={openImprintModal} className="text-white hover:text-gray-300">
                {translations.imprint}
              </button>
              <span className="text-white">||</span>
              <button onClick={openDataProtectionModal} className="text-white hover:text-gray-300">
                {translations.dataProtection}
              </button>
            </div>
            <div className="flex justify-center items-center gap-2">
              <button onClick={openTermsModal} className="text-white hover:text-gray-300">
                {translations.termsAndConditions}
              </button>
              <span className="text-white">||</span>
              <button onClick={openChatBot} className="text-white hover:text-gray-300">
                {translations.businessAssistant}
              </button>
            </div>
            <div className="flex justify-center items-center gap-2">
              <button onClick={handleMadeInGermanyHistoryClick} className="text-white hover:text-gray-300">
                {translations.madeInGermanyHistory}
              </button>
              <span className="text-white">||</span>
              <button onClick={handleCareersClick} className="text-white hover:text-gray-300">
                {translations.careers}
              </button>
            </div>
            <div className="flex justify-center items-center gap-2">
              <button onClick={handleLegalAndProtectionClick} className="text-white hover:text-gray-300">
                {translations.legalAndProtection}
              </button>
              <span className="text-white">||</span>
              <button onClick={handleStrategicInvestmentsClick} className="text-white hover:text-gray-300">
                {translations.strategicInvestments}
              </button>
            </div>
            <div className="flex justify-center items-center gap-2">
              <button onClick={handlePressContactClick} className="text-white hover:text-gray-300">
                {translations.pressContact}
              </button>
              <span className="text-white">||</span>
              <button onClick={handleFranchiseClick} className="text-white hover:text-gray-300">
                {translations.franchiseSystem}
              </button>
            </div>
          </nav>

          {/* Desktop: 4 lines */}
          <nav className="hidden sm:block text-sm mb-1">
            <button onClick={openImprintModal} className="text-white hover:text-gray-300">
              {translations.imprint}
            </button>
            <span className="mx-2 text-white">||</span>
            <button onClick={openDataProtectionModal} className="text-white hover:text-gray-300">
              {translations.dataProtection}
            </button>
            <span className="mx-2 text-white">||</span>
            <button onClick={openTermsModal} className="text-white hover:text-gray-300">
              {translations.termsAndConditions}
            </button>
            <span className="mx-2 text-white">||</span>
            <button onClick={openChatBot} className="text-white hover:text-gray-300">
              {translations.businessAssistant}
            </button>
          </nav>
          <nav className="hidden sm:block text-sm mb-1">
            <button onClick={handleMadeInGermanyHistoryClick} className="text-white hover:text-gray-300">
              {translations.madeInGermanyHistory}
            </button>
            <span className="mx-2 text-white">||</span>
            <button onClick={handleCareersClick} className="text-white hover:text-gray-300">
              {translations.careers}
            </button>
            <span className="mx-2 text-white">||</span>
            <button onClick={handleStrategicInvestmentsClick} className="text-white hover:text-gray-300">
              {translations.strategicInvestments}
            </button>
          </nav>
          <nav className="hidden sm:block text-sm mb-1">
            <button onClick={handleLegalAndProtectionClick} className="text-white hover:text-gray-300">
              {translations.legalAndProtection}
            </button>
            <span className="mx-2 text-white">||</span>
            <button onClick={handlePressContactClick} className="text-white hover:text-gray-300">
              {translations.pressContact}
            </button>
            <span className="mx-2 text-white">||</span>
            <button onClick={handleFranchiseClick} className="text-white hover:text-gray-300">
              {translations.franchiseSystem}
            </button>
          </nav>

          {/* Copyright - Responsive with two lines */}
          <p className="text-gray-500 text-sm mt-6 sm:text-sm sm:mt-0">
            {translations.copyright.split(' - ')[0]}<br />
            {translations.copyright.split(' - ').slice(1).join(' - ')}
          </p>
        </div>
      </footer>
      <ImprintModal 
        isOpen={isImprintOpen} 
        onClose={() => setIsImprintOpen(false)} 
        language={language}
      />
      <DataProtectionModal 
        isOpen={isDataProtectionOpen}
        onClose={() => setIsDataProtectionOpen(false)}
        language={language}
      />
      <TermsModal 
        isOpen={isTermsOpen}
        onClose={() => setIsTermsOpen(false)}
        language={language}
      />
      <ChatBot 
        isOpen={isChatBotOpen}
        onClose={() => setIsChatbotOpen(false)}
        language={language}
      />
    </>
  );
};

export default Footer;